import type { ExplainRequest, Explanation } from '@lyrmo/shared';
import { useMemo, useState } from 'react';
import type { RewriteClient } from '../../api/client';
import { useI18n } from '../../i18n/i18n-context';

/** The changes to explain. The reasons come in the interface's language. */
type Changes = Omit<ExplainRequest, 'language'>;
type Rewrite = Pick<ExplainRequest, 'original' | 'rewrite'>;

export interface Explain {
  (request: Changes): Promise<Explanation[]>;
  /** The explanations already in hand for a rewrite, so they show at once, without a wait. */
  readonly known?: (rewrite: Rewrite) => readonly Explanation[] | undefined;
}

/**
 * Explains the changes in a rewrite, in the interface's language, once: switching views, or back
 * to a card, reuses the answer, and switching languages asks again. Failures aren't kept, so
 * trying again asks afresh.
 */
export function useExplanations(client: RewriteClient): Explain {
  const { locale } = useI18n();
  const [cache] = useState(() => new Map<string, Promise<Explanation[]>>());
  const [answers] = useState(() => new Map<string, Explanation[]>());

  return useMemo(() => {
    const keyOf = ({ original, rewrite }: Rewrite) => JSON.stringify([locale, original, rewrite]);
    const explain = (request: Changes) => {
      const key = keyOf(request);
      const cached = cache.get(key);
      if (cached) return cached;
      const pending = client.explainChanges({ ...request, language: locale });
      cache.set(key, pending);
      pending.then(
        (explanations) => answers.set(key, explanations),
        () => cache.delete(key),
      );
      return pending;
    };
    return Object.assign(explain, {
      known: (rewrite: Rewrite) => answers.get(keyOf(rewrite)),
    });
  }, [answers, cache, client, locale]);
}
